/**
 * UPT-2079 L14b (web §5.5) — modèle PUR des pages sur mesure (`pages[].kind = "custom"`, schéma v1.2) du Concepteur : assainissement des morceaux
 * de page qu'écrit le réducteur (référence au prototype, objets lus / écrits, disposition, emplacements, actions), cascades (renommage et retrait
 * d'un objet, d'une page, d'un emplacement) et lecture des états du workflow d'un objet. Aucun composant, aucun accès réseau.
 *
 * Règle du réducteur : un morceau STRUCTURELLEMENT invalide (clé hors motif, doublon, borne dépassée, libellé sans `fr`) est refusé en bloc ;
 * une RÉFÉRENCE inconnue (objet absent du document) est retirée. Les règles qui relèvent du validateur (`page.target_unknown`,
 * `page.transition_unknown`, `page.slot_unknown`…) restent affichées sur le champ par le rapport, elles ne sont pas réécrites ici.
 */
import { MODULE_SPEC_SECTION_SELECTOR } from '../data/moduleSpecContracts';
import { ACTION_KINDS, CUSTOM_PAGE_LIMITS, LOCALES, PAGE_LAYOUTS, SLOT_BINDINGS } from './catalogDefaults';
import { asRecord, asRecords, asString, asStrings, type Json, type ModuleSpecEntity } from './specTypes';

/** Libellé par langue nettoyé : seules les locales du schéma, chaînes non vides ; `fr` requis sinon `null`. */
export const cleanI18n = (value: unknown): Record<string, string> | string | null => {
  if (typeof value === 'string') return value.length > 0 ? value : null;
  const record = asRecord(value);
  if (!record) return null;
  const out: Record<string, string> = {};
  LOCALES.forEach((locale) => {
    const text = record[locale];
    if (typeof text === 'string' && text.length > 0) out[locale] = text.slice(0, 500);
  });
  return typeof out.fr === 'string' ? out : null;
};

/** Résultat d'un assainissement : `value === undefined` retire la propriété. */
export type Sanitized<T> = { ok: true; value: T | undefined } | { ok: false };

const ok = <T,>(value: T | undefined): Sanitized<T> => ({ ok: true, value });
const refused = <T,>(): Sanitized<T> => ({ ok: false });

/** `prototypeRef` : `null` / `undefined` retire ; deux uuid requis ; `selector` au motif du schéma ; `sectionLabel` ≤ 120. */
export const sanitizePrototypeRef = (value: unknown): Sanitized<Json> => {
  if (value === null || value === undefined) return ok(undefined);
  const record = asRecord(value);
  if (!record) return refused();
  const importRef = asString(record.importRef);
  const pieceRef = asString(record.pieceRef);
  if (!CUSTOM_PAGE_LIMITS.uuidPattern.test(importRef) || !CUSTOM_PAGE_LIMITS.uuidPattern.test(pieceRef)) return refused();
  const out: Json = { importRef, pieceRef };
  const selector = asString(record.selector);
  if (selector !== '') {
    if (selector.length > CUSTOM_PAGE_LIMITS.selector || !MODULE_SPEC_SECTION_SELECTOR.test(selector)) return refused();
    out.selector = selector;
  }
  const sectionLabel = asString(record.sectionLabel).trim().slice(0, CUSTOM_PAGE_LIMITS.sectionLabel);
  if (sectionLabel !== '') out.sectionLabel = sectionLabel;
  return ok(out);
};

/** `reads` / `writes` : codes uniques, connus du document, ≤ 20 ; vide retire. Plus de 20 codes → refusé. */
export const sanitizeEntityList = (value: unknown, codes: readonly string[]): Sanitized<string[]> => {
  if (value === null || value === undefined) return ok(undefined);
  if (!Array.isArray(value)) return refused();
  const list = [...new Set(asStrings(value).filter((code) => codes.includes(code)))];
  if (list.length > CUSTOM_PAGE_LIMITS.entities) return refused();
  return ok(list.length > 0 ? list : undefined);
};

/** `layout` : valeur du schéma ; `null` retire. */
export const sanitizeLayout = (value: unknown): Sanitized<string> => {
  if (value === null || value === undefined || value === '') return ok(undefined);
  return typeof value === 'string' && PAGE_LAYOUTS.includes(value) ? ok(value) : refused();
};

/** `slots` : ≤ 12, clés au motif et uniques, libellé avec `fr`, objet connu (sinon retiré, la liaison redevient `none`). */
export const sanitizeSlots = (value: unknown, codes: readonly string[]): Sanitized<Json[]> => {
  if (value === null || value === undefined) return ok(undefined);
  if (!Array.isArray(value) || value.length > CUSTOM_PAGE_LIMITS.slots) return refused();
  const seen = new Set<string>();
  const out: Json[] = [];
  for (const raw of value) {
    const slot = asRecord(raw);
    if (!slot) return refused();
    const key = asString(slot.key);
    if (!CUSTOM_PAGE_LIMITS.keyPattern.test(key) || seen.has(key)) return refused();
    seen.add(key);
    const label = cleanI18n(slot.label);
    if (!label) return refused();
    const next: Json = { key, label };
    const description = asString(slot.description).slice(0, CUSTOM_PAGE_LIMITS.slotDescription);
    if (description !== '') next.description = description;
    const entity = asString(slot.entity);
    if (entity !== '' && codes.includes(entity)) next.entity = entity;
    const binding = asString(slot.binding);
    if (binding !== '') {
      if (!SLOT_BINDINGS.includes(binding)) return refused();
      next.binding = binding !== 'none' && !next.entity ? 'none' : binding;
    }
    out.push(next);
  }
  return ok(out.length > 0 ? out : undefined);
};

/** `actions` : ≤ 20, clés au motif et uniques, libellé avec `fr`, genre du schéma ; références aux motifs, objet inconnu retiré. */
export const sanitizeActions = (value: unknown, codes: readonly string[]): Sanitized<Json[]> => {
  if (value === null || value === undefined) return ok(undefined);
  if (!Array.isArray(value) || value.length > CUSTOM_PAGE_LIMITS.actions) return refused();
  const seen = new Set<string>();
  const out: Json[] = [];
  for (const raw of value) {
    const action = asRecord(raw);
    if (!action) return refused();
    const key = asString(action.key);
    if (!CUSTOM_PAGE_LIMITS.keyPattern.test(key) || seen.has(key)) return refused();
    seen.add(key);
    const label = cleanI18n(action.label);
    if (!label) return refused();
    const kind = asString(action.kind);
    if (!ACTION_KINDS.includes(kind)) return refused();
    const next: Json = { key, label, kind };
    const entity = asString(action.entity);
    if (entity !== '' && codes.includes(entity)) next.entity = entity;
    const targetPage = asString(action.targetPage);
    if (targetPage !== '') {
      if (!CUSTOM_PAGE_LIMITS.refPattern.test(targetPage)) return refused();
      next.targetPage = targetPage;
    }
    const transition = asString(action.transition);
    if (transition !== '') {
      if (!CUSTOM_PAGE_LIMITS.refPattern.test(transition)) return refused();
      next.transition = transition;
    }
    const slot = asString(action.slot);
    if (slot !== '') {
      if (!CUSTOM_PAGE_LIMITS.keyPattern.test(slot)) return refused();
      next.slot = slot;
    }
    const description = asString(action.description).slice(0, CUSTOM_PAGE_LIMITS.actionDescription);
    if (description !== '') next.description = description;
    out.push(next);
  }
  return ok(out.length > 0 ? out : undefined);
};

/** Une action ne cite qu'un emplacement qui existe : sinon `slot` est retiré (appelé après un patch de `slots`). */
export const reconcileActionSlots = (page: Json): void => {
  const slotKeys = new Set(asRecords(page.slots).map((slot) => asString(slot.key)));
  if (!Array.isArray(page.actions)) return;
  asRecords(page.actions).forEach((action) => {
    if (typeof action.slot === 'string' && !slotKeys.has(action.slot)) delete action.slot;
  });
};

/** Vrai quand la page porte une propriété du schéma v1.2 (le document passe alors en « 1.2 »). */
export const usesCustomPageProperties = (page: Json): boolean =>
  page.kind === 'custom' ||
  ['prototypeRef', 'reads', 'writes', 'layout', 'slots', 'actions'].some((key) => key in page);

/* ── Cascades ────────────────────────────────────────────────────────────── */

const mapEntity = (owner: Json, key: string, previous: string, next: string | null): void => {
  if (owner[key] !== previous) return;
  if (next === null) delete owner[key];
  else owner[key] = next;
};

/** Renomme (`next` non nul) ou retire (`next` nul) un objet dans `reads`, `writes`, `slots[].entity` et `actions[].entity` de toutes les pages. */
export const cascadeEntityInPages = (pages: Json[], previous: string, next: string | null): void => {
  pages.forEach((page) => {
    (['reads', 'writes'] as const).forEach((key) => {
      if (!Array.isArray(page[key])) return;
      const mapped = asStrings(page[key])
        .map((code) => (code === previous ? next : code))
        .filter((code): code is string => code !== null);
      const unique = [...new Set(mapped)];
      if (unique.length === 0) delete page[key];
      else page[key] = unique;
    });
    asRecords(page.slots).forEach((slot) => {
      if (slot.entity !== previous) return;
      mapEntity(slot, 'entity', previous, next);
      if (next === null) slot.binding = 'none';
    });
    asRecords(page.actions).forEach((action) => {
      if (action.entity !== previous) return;
      mapEntity(action, 'entity', previous, next);
      if (next === null) delete action.transition;
    });
  });
};

/** Renomme (`next` non nul) ou retire (`next` nul) une clé de page dans `actions[].targetPage` de toutes les pages. */
export const cascadePageKeyInActions = (pages: Json[], previous: string, next: string | null): void => {
  pages.forEach((page) => {
    asRecords(page.actions).forEach((action) => {
      if (action.targetPage !== previous) return;
      if (next === null) delete action.targetPage;
      else action.targetPage = next;
    });
  });
};

/* ── Lectures pour l'éditeur ─────────────────────────────────────────────── */

/** Clés des états du workflow d'un objet (vide si l'objet n'a pas de cycle de vie). */
export const workflowStateKeys = (entity: ModuleSpecEntity | Json | null | undefined): string[] =>
  asRecords(asRecord(entity?.workflow)?.states)
    .map((state) => asString(state.key))
    .filter((key) => key !== '');

/** Premier numéro libre d'une clé d'emplacement ou d'action (`slot-1`, `action-2`…), au motif du schéma. */
export const nextFreeIndex = (prefix: string, taken: readonly string[]): number => {
  let index = 1;
  while (taken.includes(`${prefix}-${index}`)) index += 1;
  return index;
};

/** Nouvel emplacement : clé libre, libellé français provisoire (comme `newPage`, le Maker le renomme). */
export const newSlot = (taken: readonly string[]): { key: string; label: { fr: string } } => {
  const index = nextFreeIndex('slot', taken);
  return { key: `slot-${index}`, label: { fr: `Emplacement ${index}` } };
};

/** Nouvelle action : clé libre, libellé français provisoire, type « sur mesure » (désactivée, à réaliser). */
export const newAction = (taken: readonly string[]): { key: string; label: { fr: string }; kind: string } => {
  const index = nextFreeIndex('action', taken);
  return { key: `action-${index}`, label: { fr: `Action ${index}` }, kind: 'custom' };
};
